import { useCallback, useMemo, useState } from 'react';
import { Nav } from 'react-bootstrap';
import { Redirect, Route, Switch, useHistory, useLocation } from 'react-router';
import i18n from 'i18n';
import Select from 'components/common/Select/Select';
import { Page } from 'components/Page/Page';
import { LoadingIndicator } from 'components/common/LoadingIndicator';
import {
  DeviceFilterField,
  DeviceFilterOptionsState,
  findFilterOptions,
  DEVICE_FILTER_HINT_I18N
} from './deviceFilters';
import {
  DeviceFilterSelect,
  DeviceFilterTag,
  DeviceFilterTags
} from './DeviceFilterSelect';
import { useDeviceManagementModel } from './DeviceManagementModel';
import { useDeviceListModel } from './DeviceListModel';
import { useFutureAvailabilityModel } from './FutureAvailabilityModel';
import { DeviceList } from './DeviceList';
import { DeviceImportModal } from './DeviceImportModal';
import { DeviceListModeBand } from './DeviceListModeBand';
import { FutureAvailability } from './FutureAvailability';
import styles from './deviceManagement.module.scss';

export const DEVICE_MANAGEMENT_PATH = '/device-management';

export enum DeviceManagementTab {
  LIST = 'list',
  AVAILABILITY = 'availability'
}

export const deviceManagementTabPath = (tab: DeviceManagementTab) =>
  `${DEVICE_MANAGEMENT_PATH}/${tab}`;

/**
 * 四個條件的順序與標題照設計稿。前兩個的選項來源是兩層的（縣市 → 區、
 * 類別 → 子類別），後兩個是單層 —— 差別在資料，不在這張表。
 */
const MULTI_FILTERS: Array<{ field: DeviceFilterField; labelKey: string }> = [
  {
    field: DeviceFilterField.PLACE_CATEGORY,
    labelKey: 'limitation.labels.outdoorPlaceCategories'
  },
  {
    field: DeviceFilterField.ADMINISTRATIVE_AREA,
    labelKey: 'limitation.labels.administrativeArea'
  },
  {
    field: DeviceFilterField.REGION,
    labelKey: 'deviceManagement.labels.region'
  },
  {
    field: DeviceFilterField.HEALTH_STATE,
    labelKey: 'deviceManagement.headers.healthState'
  }
];

export const DeviceManagement: React.FC = () => {
  const {
    loading,
    salesChannelOptions,
    salesChannel,
    channelDeliveryUnit,
    channelDeliveryUnitLabel,
    onSalesChannelChange,
    filter,
    onFilterChange,
    onClearFilter,
    filterOptions,
    filterOptionsState,
    importList,
    matchedUnits,
    storeNums,
    setImportList,
    onExitListMode,
    buildLastSyncedLabel
  } = useDeviceManagementModel();
  const history = useHistory();
  const location = useLocation();
  const [showImport, setShowImport] = useState(false);

  const activeTab = useMemo(
    () =>
      location.pathname.startsWith(
        deviceManagementTabPath(DeviceManagementTab.AVAILABILITY)
      )
        ? DeviceManagementTab.AVAILABILITY
        : DeviceManagementTab.LIST,
    [location.pathname]
  );

  // 兩個分頁的查詢狀態都掛在這一層，而不是各自的分頁元件裡：換分頁時 Switch 會把
  // 分頁元件卸載，狀態放在裡面就會跟著沒了，回來就一定要重查一次。掛在這裡之後，
  // 條件沒變的那一次切換不會再送請求（各自的 model 會比對條件），條件變了則照查。
  const listModel = useDeviceListModel({
    salesChannel,
    channelDeliveryUnit,
    filter,
    storeNums,
    active: activeTab === DeviceManagementTab.LIST
  });
  const availabilityModel = useFutureAvailabilityModel({
    salesChannel,
    channelDeliveryUnit,
    filter,
    storeNums,
    active: activeTab === DeviceManagementTab.AVAILABILITY
  });

  const isList = activeTab === DeviceManagementTab.LIST;
  const activeModel = isList ? listModel : availabilityModel;
  // 「最後同步」畫在標題列，與分頁無關。後端在清單為空時會整個省略這個欄位
  // （見 core/deviceManagement/DeviceManagement 的 DeviceUnitList），只看目前分頁
  // 的話，另一個分頁明明已經拿到時間，切過去卻是一片空白。
  const lastSyncedLabel = buildLastSyncedLabel(
    activeModel.lastSyncedAt ??
      (isList ? availabilityModel.lastSyncedAt : listModel.lastSyncedAt)
  );

  // 分頁放在網址上，而不是元件 state：頁面要能分享連結，而共用的篩選條件
  // 由這一層持有，換分頁時這些條件不會被重設。
  const onTabSelect = useCallback(
    (tab: string | null) => {
      if (!tab) {
        return;
      }
      history.push(deviceManagementTabPath(tab as DeviceManagementTab));
    },
    [history]
  );

  const filterTags = useMemo<DeviceFilterTag[]>(
    () =>
      MULTI_FILTERS.flatMap(({ field }) =>
        findFilterOptions(filterOptions[field], filter[field]).map(
          (option) => ({ ...option, field })
        )
      ),
    [filterOptions, filter]
  );

  const onFilterTagsChange = useCallback(
    (remaining: DeviceFilterTag[]) => {
      const kept = new Set(
        remaining.map((tag) => `${tag.field}|${String(tag.value)}`)
      );
      MULTI_FILTERS.forEach(({ field }) => {
        const next = filter[field].filter((value) =>
          kept.has(`${field}|${value}`)
        );
        if (next.length !== filter[field].length) {
          onFilterChange(field, next);
        }
      });
    },
    [filter, onFilterChange]
  );

  const onOpenImport = useCallback(() => setShowImport(true), []);

  const renderFilterArea = useCallback(
    () => (
      <>
        {lastSyncedLabel && (
          <div className={styles.lastSynced} data-testid='last-synced'>
            {lastSyncedLabel}
          </div>
        )}
        <div className={styles.channelRow}>
          <label className={styles.filterTitle} htmlFor='salesChannel'>
            {i18n.t('report.labels.salesChannel')}
          </label>
          <div className={styles.channelSelect}>
            <Select
              name='salesChannel'
              options={salesChannelOptions}
              value={salesChannel}
              simpleValue
              onChange={onSalesChannelChange}
              placeholder={i18n.t('common.placeholder.pleaseSelect')}
              componentWidthFitParent
            />
          </div>
          {channelDeliveryUnitLabel && (
            <span
              className={styles.channelHint}
              data-testid='channel-delivery-unit'
            >
              {channelDeliveryUnitLabel}
            </span>
          )}
        </div>
        <div className={styles.filterRow}>
          {importList && (
            <span className={styles.filterHint}>
              {i18n.t('deviceManagement.hints.filterWithinList')}
            </span>
          )}
          {MULTI_FILTERS.map(({ field, labelKey }) => (
            <DeviceFilterSelect
              key={field}
              name={field}
              label={i18n.t(labelKey)}
              hint={i18n.t(DEVICE_FILTER_HINT_I18N[field])}
              options={filterOptions[field]}
              // 狀態的選項是前端自己組的常數，沒有取回來這件事。
              optionsState={
                field === DeviceFilterField.HEALTH_STATE
                  ? DeviceFilterOptionsState.LOADED
                  : filterOptionsState
              }
              disabled={salesChannel === undefined}
              values={filter[field]}
              onChange={(values: string[]) => onFilterChange(field, values)}
            />
          ))}
          {filterTags.length > 0 && (
            <span className={styles.clearFilter} onClick={onClearFilter}>
              {i18n.t('deviceManagement.buttons.clearFilter')}
            </span>
          )}
          {/* 已選的條件跟在條件本身後面，同一個會換行的容器裡：另起一排會讓
              「這幾個標籤是誰選出來的」少一個線索，而放在同一排也不會把它撐寬。 */}
          {filterTags.length > 0 && (
            <DeviceFilterTags
              values={filterTags}
              onChange={onFilterTagsChange}
            />
          )}
        </div>
        <Nav activeKey={activeTab} onSelect={onTabSelect}>
          <Nav.Item>
            <Nav.Link eventKey={DeviceManagementTab.LIST}>
              {i18n.t('deviceManagement.tabs.list')}
            </Nav.Link>
          </Nav.Item>
          <Nav.Item>
            <Nav.Link eventKey={DeviceManagementTab.AVAILABILITY}>
              {i18n.t('deviceManagement.tabs.availability')}
            </Nav.Link>
          </Nav.Item>
        </Nav>
      </>
    ),
    [
      lastSyncedLabel,
      salesChannelOptions,
      salesChannel,
      onSalesChannelChange,
      channelDeliveryUnitLabel,
      importList,
      filterOptions,
      filterOptionsState,
      filter,
      onFilterChange,
      filterTags,
      onFilterTagsChange,
      onClearFilter,
      activeTab,
      onTabSelect
    ]
  );

  const renderDeviceList = useCallback(
    () => (
      <DeviceList
        salesChannel={salesChannel}
        channelDeliveryUnit={channelDeliveryUnit}
        model={listModel}
        onOpenImport={onOpenImport}
        listFileName={importList?.fileName}
      />
    ),
    [
      salesChannel,
      channelDeliveryUnit,
      listModel,
      onOpenImport,
      importList?.fileName
    ]
  );

  const renderFutureAvailability = useCallback(
    () => (
      <FutureAvailability
        salesChannel={salesChannel}
        channelDeliveryUnit={channelDeliveryUnit}
        model={availabilityModel}
        onOpenImport={onOpenImport}
        listFileName={importList?.fileName}
      />
    ),
    [
      salesChannel,
      channelDeliveryUnit,
      availabilityModel,
      onOpenImport,
      importList?.fileName
    ]
  );

  return (
    <Page
      title={i18n.t('deviceManagement.title')}
      renderInTopArea={renderFilterArea}
    >
      {loading && <LoadingIndicator />}
      <div className={styles.deviceManagement}>
        {importList && (
          <DeviceListModeBand
            list={importList}
            hitCount={activeModel.totalCount}
            matchedUnits={matchedUnits}
            onExit={onExitListMode}
          />
        )}
        <Switch>
          <Route
            path={deviceManagementTabPath(DeviceManagementTab.LIST)}
            render={renderDeviceList}
          />
          <Route
            path={deviceManagementTabPath(DeviceManagementTab.AVAILABILITY)}
            render={renderFutureAvailability}
          />
          <Redirect to={deviceManagementTabPath(DeviceManagementTab.LIST)} />
        </Switch>
      </div>
      {showImport && (
        <DeviceImportModal
          onImport={setImportList}
          onClose={() => setShowImport(false)}
        />
      )}
    </Page>
  );
};
